import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ModalController, PopoverController } from '@ionic/angular';
import { HandleSubscriptionsComponent } from 'src/app/appDirectives/handle-subscriptions.directive';
import { RestaurantInfoResolver } from 'src/app/appResolvers/restaurant-info.resolver';
import { OrderService } from 'src/app/appServices/order.service';
import { ToastMessageService } from 'src/app/core/utilities/toast-message.service';
import { CustomDiscountComponent } from 'src/app/includes/popups/custom-discount/custom-discount.component';
import { ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CashPaymentJournalService } from 'src/app/core/payments/cash-payment-journal.service';
import { CartResolver } from 'src/app/appResolvers/cart.resolver';

@Component({
    selector: 'app-cash-payment',
    templateUrl: './cash-payment.component.html',
    styleUrls: ['./cash-payment.component.scss'],
    standalone: false
})
export class CashPaymentComponent
  extends HandleSubscriptionsComponent
  implements OnInit {
  @Input() dataPayload: any;
  basicInfo: any;
  amountForm: UntypedFormGroup;
  remainingChanges = 0;
  loading: boolean = false;
  orderByLoyaltyPoints: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private infoStorage: RestaurantInfoResolver,
    private _toastMessageService: ToastMessageService,
    private orderService: OrderService,
    private _fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private cashPaymentJournal: CashPaymentJournalService,
    private cartResolver: CartResolver,
  ) {
    super();
  }

  ngOnInit() {
    console.log(this.dataPayload);
    this.getBasicInfo();
    this.checkIfOrderByLoyaltyPoints();
    this.amountForm = this._fb.group({
      amount: [
        '',
        [Validators.required, amountVaidator(this.dataPayload.totalAmount)],
      ],
    });
  }

  /**
   * Check if order by loyalty points
   */
  checkIfOrderByLoyaltyPoints() {
    if (this.dataPayload.paymentMethod?.title === 'Loyalty Points') {
      this.orderByLoyaltyPoints = true;
      this.payByCash();
    }
  }

  closeModal(response) {
    this.modalCtrl.dismiss(response);
  }

  /**
   * pay by cash
   */
  async payByCash() {
    if (this.loading) {
      return;
    }
    this.loading = true;
    try {
      if (this.dataPayload.id) {
        await this.markOrderAsPaid();
      } else {
        await this.createGuestCheckout(this.dataPayload);
      }
    } catch (err) {
      const errorMessage = this.translateService.instant(
        'An error occurred. Please contact support@tapgo.ch to resolve this issue.',
      );
      this._toastMessageService.errorToastWithClose(errorMessage);
      console.error('Payment failed:', err);
      this.loading = false;
    }
  }

  /**
   * Create guest checkout
   * @param order
   */
  async createGuestCheckout(order) {
    console.log('Guest Checkout', order);
    order.platformFee = 0;
    order.processingFee = 0;

    const res = await this.cashPaymentJournal.submitCheckout(
      order,
      order.orderIdList?.length > 0,
      await this.cartResolver.getOrCreatePaymentSaleUuid(),
    );
    order.id = res.id;
    order.change = this.remainingChanges;
    this.closeModal(order);
    console.log('Payment successful:', res);
  }


  /**
   * mark order as paid
   */
  async markOrderAsPaid() {
    // Initialize the temporary payload with basic data
    let temp: any = {
      emailId: this.dataPayload.emailId,
      mobileNumber: this.dataPayload.mobileNumber,
      processingFee: this.dataPayload.processingFee,
    };

    // If the payment method is Loyalty Points, modify the payload
    if (this.dataPayload.paymentMethod.title === 'Loyalty Points') {
      temp = {
        amountSpend: 0.1,
        loyaltyPointsSpent: this.dataPayload.loyaltyPointsSpent,
      };
    }

    // Check if customer information exists and add it to the payload
    if (this.dataPayload.customer) {
      temp.customer = this.dataPayload.customer;
      console.log('Customer information added to payload:', temp.customer);
    }

    console.log('Mark Order as Paid', this.dataPayload);

    const isKiosk =
      `${this.dataPayload?.channel || ''}`.trim().toLowerCase() ===
      'kiosk';
    const res = await this.cashPaymentJournal.submitMarkPaid(
      this.dataPayload.id,
      temp,
      isKiosk ? ['KOT'] : ['BILLING'],
    );
    this.dataPayload.paid = true;
    this.dataPayload.paymentType = 'cash';
    this.dataPayload.change = this.remainingChanges;
    this.closeModal(this.dataPayload);
    console.log('Order marked as paid:', res);
  }

  async openCustomDiscountModal() {
    try {
      this.amountForm.controls['amount'].reset();
      this.calculateChanges();

      const modal = await this.modalCtrl.create({
        component: CustomDiscountComponent,
        componentProps: {
          currentTotal: this.dataPayload.totalAmount,
        },
      });

      await modal.present();

      const { data } = await modal.onDidDismiss();
      if (data) {
        console.log(data);
        const discount = data;
        this.applyDiscountToTotal(discount);
      }
    } catch (error) {
      console.error('Error opening custom discount modal:', error);
    }
  }

  applyDiscountToTotal(discount) {
    let discountAmount = 0;

    // Calculate the discount amount based on the type of discount
    if (discount.discountType === 'percentage') {
      discountAmount = this.dataPayload.totalAmount * (discount.value / 100);
      this.dataPayload.totalAmount -= discountAmount;
    } else if (discount.discountType === 'custom') {
      discountAmount = discount.value;
      this.dataPayload.totalAmount =
        this.dataPayload.totalAmount - discountAmount;
    }

    // Log the payload and orderDetailsModelList before applying the discount
    console.log('Payload: ', this.dataPayload);
    console.log('Before discount:', this.dataPayload.orderDetailsModelList);

    // Calculate the original total and apply discount proportionally
    const originalTotal = this.dataPayload.orderDetailsModelList.reduce(
      (acc, item) => acc + item.basePrice * item.quantity,
      0
    );

    let totalCalculated = 0;

    // Apply the discount proportionally to each item, rounding to 2 decimal places
    this.dataPayload.orderDetailsModelList.forEach((item, index, arr) => {
      const itemTotalPrice = item.basePrice * item.quantity;
      const itemProportion = itemTotalPrice / originalTotal;
      let itemDiscount = discountAmount * itemProportion;

      // Use discountedPrice and ensure it's a number with 2 decimal places
      item.discountedPrice = parseFloat(
        (
          (item.basePrice * item.quantity - itemDiscount) /
          item.quantity
        ).toFixed(2)
      );
      totalCalculated += item.discountedPrice * item.quantity;

      // Adjust the last item if necessary to match the total amount exactly
      if (
        index === arr.length - 1 &&
        totalCalculated !== this.dataPayload.totalAmount
      ) {
        const difference = this.dataPayload.totalAmount - totalCalculated;
        item.discountedPrice = parseFloat(
          (
            (item.discountedPrice * item.quantity + difference) /
            item.quantity
          ).toFixed(2)
        );
      }
    });

    // Log the data after applying the discount and rounding
    console.log('After discount:', this.dataPayload.orderDetailsModelList);

    // Store the discount amount in dataPayload
    this.dataPayload.discount = discountAmount;

    // Reset the amount form control after updating the total amount
    this.amountForm.controls['amount'].reset();

    // Update the validator with the updated totalAmount
    this.amountForm.controls['amount'].setValidators([
      Validators.required,
      amountVaidator(this.dataPayload.totalAmount), // Use the modified totalAmount
    ]);

    // Re-evaluate the form's validity and trigger change detection
    this.amountForm.controls['amount'].updateValueAndValidity();
    this.cdr.detectChanges(); // Ensure Angular detects the change and updates the UI
  }

  removeCents() {
    this.dataPayload.totalAmount = Math.floor(this.dataPayload.totalAmount);
    this.amountForm.controls['amount'].reset();
    this.calculateChanges();
    this.cdr.detectChanges(); // Ensure Angular detects the change and updates the UI
  }

  /**
   * calculate changes
   */
  calculateChanges() {
    const amountGivenIsLess =
      this.dataPayload.totalAmount > this.amountForm.value.amount;
    if (this.amountForm.value.amount != null && !amountGivenIsLess) {
      this.remainingChanges =
        this.amountForm.value.amount - this.dataPayload.totalAmount;
    } else {
      this.remainingChanges = 0;
    }
  }

  /**
   * get basic information
   */
  getBasicInfo() {
    this.handleSubscription(this.infoStorage.restaurantBasicInfo, (res) => {
      if (res) {
        this.basicInfo = res;
      }
    });
  }
}

export function amountVaidator(currentTotalAmount): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const forbidden = control.value < currentTotalAmount;
    return forbidden ? { forbiddenName: { value: control.value } } : null;
  };
}
